import { Sentry } from './sentry';

// Log levels in ascending order of severity
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

// Context metadata that can be attached to log entries
export interface LogContext {
  [key: string]: unknown;
}

// Logger configuration
interface LoggerConfig {
  minLevel: LogLevel;
  enableSentry: boolean;
}

// Determine minimum log level based on environment
const getMinLogLevel = (): LogLevel => {
  const envLevel = import.meta.env.VITE_LOG_LEVEL;
  if (envLevel && Object.values(LogLevel).includes(envLevel as LogLevel)) {
    return envLevel as LogLevel;
  }
  return import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.INFO;
};

// Logger configuration based on environment
const config: LoggerConfig = {
  minLevel: getMinLogLevel(),
  enableSentry: !import.meta.env.DEV && !!import.meta.env.VITE_SENTRY_DSN,
};

// Log level priority for filtering
const levelPriority: Record<LogLevel, number> = {
  [LogLevel.DEBUG]: 0,
  [LogLevel.INFO]: 1,
  [LogLevel.WARN]: 2,
  [LogLevel.ERROR]: 3,
};

// Check if a log level should be logged based on configuration
const shouldLog = (level: LogLevel): boolean => {
  return levelPriority[level] >= levelPriority[config.minLevel];
};

// Format log message with context
const formatLogMessage = (message: string, context?: LogContext): string => {
  if (!context || Object.keys(context).length === 0) {
    return message;
  }
  return `${message} | Context: ${JSON.stringify(context)}`;
};

// Core logging function
const log = (level: LogLevel, message: string, context?: LogContext) => {
  if (!shouldLog(level)) {
    return;
  }

  const formattedMessage = formatLogMessage(message, context);

  // In development, use console with colors/formatting
  if (import.meta.env.DEV) {
    const consoleMethod = level === LogLevel.DEBUG ? 'log' : level;
    const prefix = `[${level.toUpperCase()}]`;

    // eslint-disable-next-line no-console
    console[consoleMethod](prefix, message, context || '');
    return;
  }

  // In production, use structured logging
  if (level === LogLevel.ERROR && config.enableSentry) {
    // Send errors to Sentry with context
    Sentry.captureException(new Error(message), {
      level: 'error',
      extra: context,
    });
  } else if (level === LogLevel.WARN && config.enableSentry) {
    // Send warnings to Sentry as messages
    Sentry.captureMessage(message, {
      level: 'warning',
      extra: context,
    });
  }

  // Always log to console in production (but it will be minimal)
  // eslint-disable-next-line no-console
  console[level === LogLevel.DEBUG ? 'log' : level](formattedMessage);
};

// Public logger API
export const logger = {
  /**
   * Log debug information (development only)
   * @param message - Debug message
   * @param context - Optional context metadata
   */
  debug: (message: string, context?: LogContext) => {
    log(LogLevel.DEBUG, message, context);
  },

  /**
   * Log informational messages
   * @param message - Info message
   * @param context - Optional context metadata
   */
  info: (message: string, context?: LogContext) => {
    log(LogLevel.INFO, message, context);
  },

  /**
   * Log warning messages (sent to Sentry in production)
   * @param message - Warning message
   * @param context - Optional context metadata
   */
  warn: (message: string, context?: LogContext) => {
    log(LogLevel.WARN, message, context);
  },

  /**
   * Log error messages (sent to Sentry in production)
   * @param message - Error message
   * @param error - Optional error object
   * @param context - Optional context metadata
   */
  error: (message: string, error?: Error | unknown, context?: LogContext) => {
    const fullContext = { ...context };

    // Include error details in context
    if (error instanceof Error) {
      fullContext.error = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };

      // Send the actual error to Sentry for better stack traces
      if (config.enableSentry) {
        Sentry.captureException(error, {
          extra: { logMessage: message, ...context },
        });
      }
    } else if (error) {
      fullContext.error = error;
    }

    log(LogLevel.ERROR, message, fullContext);
  },
};
